import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { useAdminAuth } from "./AuthContext";
import { useToast } from "../hooks/use-toast";

export default function CustomerEditModal({ open, onOpenChange, customer, onSaved }) {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && customer) {
      setForm({
        name: customer.name || "",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        suburb: customer.suburb || "",
      });
    }
  }, [open, customer]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!customer) return;
    setSubmitting(true);
    try {
      const r = await authAxios.patch(
        `/customers/${encodeURIComponent(customer.key)}`,
        form
      );
      toast({
        title: "Customer updated",
        description: `Applied to ${r.data.job_count} job${r.data.job_count === 1 ? "" : "s"}`,
      });
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="customer-edit-modal">
        <DialogHeader>
          <DialogTitle>Edit customer</DialogTitle>
          <DialogDescription>
            Changes apply to every booking for this customer ({customer?.job_count || 0} job
            {customer?.job_count === 1 ? "" : "s"}).
          </DialogDescription>
        </DialogHeader>

        {customer && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-slate-600 mb-1 block">Name</Label>
              <Input
                value={form.name || ""}
                onChange={(e) => update("name", e.target.value)}
                data-testid="cust-edit-name"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Phone</Label>
              <Input
                value={form.phone || ""}
                onChange={(e) => update("phone", e.target.value)}
                data-testid="cust-edit-phone"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Email</Label>
              <Input
                type="email"
                value={form.email || ""}
                onChange={(e) => update("email", e.target.value)}
                data-testid="cust-edit-email"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Address</Label>
              <Input
                value={form.address || ""}
                onChange={(e) => update("address", e.target.value)}
                data-testid="cust-edit-address"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Suburb</Label>
              <Input
                value={form.suburb || ""}
                onChange={(e) => update("suburb", e.target.value)}
                data-testid="cust-edit-suburb"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={submitting}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="cust-edit-save-btn"
          >
            {submitting && <Loader2 className="animate-spin mr-2" size={14} />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
